import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  BATCH_SIZE,
  computeFakeItemsLastRow,
  computeNewsPaginationPages,
  getNewsPageUrl,
} from './newsUtils';

export const measureNewestExtraSize = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const newestItem = document.querySelector(
    '.news-item-container:nth-child(1)',
  );
  const normalItem = document.querySelector(
    '.news-item-container:nth-child(2)',
  );

  if (!newestItem || !normalItem || !normalItem.offsetWidth) {
    return 0;
  }

  return Math.trunc(newestItem.offsetWidth / normalItem.offsetWidth) - 1;
};

export const measureRowSize = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const normalItem = document.querySelector(
    '.news-item-container:nth-child(2)',
  );
  if (!normalItem || !normalItem.offsetWidth) {
    return 0;
  }

  return Math.trunc(window.outerWidth / normalItem.offsetWidth);
};

export const useNewsListingPagination = (content) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstPage, setFirstPage] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [newestExtraSize, setNewestExtraSize] = useState(0);
  const [fakeItemsLastRow, setFakeItemsLastRow] = useState(0);
  const abortRef = useRef(null);
  const activePageRef = useRef(1);
  const fetchPageRef = useRef(null);

  const itemsTotal = content?.items_total ?? content?.items?.length ?? 0;
  const totalPages = useMemo(
    () => computeNewsPaginationPages(itemsTotal, BATCH_SIZE, newestExtraSize),
    [itemsTotal, newestExtraSize],
  );

  useEffect(() => {
    activePageRef.current = activePage;
  }, [activePage]);

  const fetchPage = useCallback(
    (page, previousPage = page, currentNewestExtraSize = newestExtraSize) => {
      if (!content?.['@id']) {
        setLoading(false);
        return;
      }

      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;

      const url = getNewsPageUrl(
        content['@id'],
        page,
        BATCH_SIZE,
        currentNewestExtraSize,
      );
      const isFirstPage = page === 1;
      const pageCount = computeNewsPaginationPages(
        itemsTotal,
        BATCH_SIZE,
        currentNewestExtraSize,
      );
      const isLastPage = page === pageCount;

      setLoading(true);

      fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`News pagination failed: ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          if (controller.signal.aborted) {
            return;
          }

          const nextItems = data.items || [];
          const rowSize = measureRowSize();
          const fakeItems = computeFakeItemsLastRow({
            itemsLength: nextItems.length,
            batchSize: BATCH_SIZE,
            newestExtraSize: currentNewestExtraSize,
            isFirstPage,
            isLastPage,
            rowSize,
          });

          setItems(nextItems);
          setActivePage(page);
          setFirstPage(isFirstPage);
          setNewestExtraSize(currentNewestExtraSize);
          setFakeItemsLastRow(fakeItems);
          setLoading(false);
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            return;
          }
          setActivePage(previousPage);
          setLoading(false);
        });
    },
    [content, itemsTotal, newestExtraSize],
  );

  fetchPageRef.current = fetchPage;

  useEffect(() => {
    if (content?.['@id']) {
      fetchPageRef.current(1);
    }
  }, [content]);

  useEffect(() => {
    if (typeof window === 'undefined' || !items.length) {
      return undefined;
    }

    const updateLayout = () => {
      if (activePageRef.current !== 1) {
        return;
      }

      const extraSize = measureNewestExtraSize();
      if (extraSize !== newestExtraSize) {
        fetchPageRef.current(1, 1, extraSize);
      }
    };

    updateLayout();
    window.addEventListener('load', updateLayout);
    window.addEventListener('resize', updateLayout);

    return () => {
      window.removeEventListener('load', updateLayout);
      window.removeEventListener('resize', updateLayout);
    };
  }, [items.length, newestExtraSize]);

  useEffect(
    () => () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    },
    [],
  );

  const handlePaginationChange = (_, { activePage: nextPage }) => {
    if (nextPage === activePage) {
      return;
    }
    fetchPage(nextPage, activePage);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  return {
    items,
    loading,
    firstPage,
    activePage,
    fakeItemsLastRow,
    itemsTotal,
    totalPages,
    handlePaginationChange,
  };
};
